import { useCallback, useEffect, useRef, useState } from "react";

/* One live stream of a server run (a trial run, a generation, an App Page
   run, the recorder), with what a bare WebSocket never did here (FE-05):

   - It closes when the screen goes (`useRunSocket`, `useRunSockets`).
   - A drop is not the run's end. The run goes on on the server, so a close
     without the run's end event first asks the HTTP state (`poll`) whether
     it ended meanwhile, then reconnects with backoff (3 tries: 1 s, 2 s,
     4 s), then follows the run over HTTP (`poll`, every `pollMs`).
   - Only the end event (`isEnd`, default `done`) or the HTTP state ends it.
     A socket close never decides a run's result.
   - Signed out: a socket without a session is refused before it connects
     (the server closes with 4401, but a browser usually reports 1006), so the
     close code is not relied on. After the retries the poll is an ordinary
     `fetch`, its 401 goes through apiShim, and that sends the user to the
     login page.

   Status: connecting → open → (reconnecting → open)* → polling → ended | lost.
   `closed` once the caller closes it. */

export const SOCKET_OPEN = 1;
const SOCKET_CLOSED = 3;

/* The server answers a socket for a run it doesn't hold (lost on a restart)
   with this, then closes. On a reconnect that's about the connection, not
   the run's result, so the caller never sees it. */
const isUnknownRun = (ev) => ev?.type === "error" && /^unknown (run|session)$/i.test(String(ev.message || ""));

const defaultIsEnd = (ev) => ev?.type === "done";

/* Opens the stream and returns a handle shaped like a WebSocket where the
   screens used one: `send`, `close`, `readyState`, plus `status`.

   options:
     onEvent(ev)       each parsed message (not JSON → skipped)
     onStatus(status)  each status change (not after `close()`)
     onEnd({reason, error})  once: reason "done" (end event or HTTP state) or
                       "lost" (no poll, or the poll kept failing)
     onOpen()          each time a socket opens (heartbeats)
     isEnd(ev)         the run's end event, default type "done"
     poll({delivered}) async, reads the HTTP state: {events?, done?, lost?}.
                       It must only read: the hook delivers what it returns.
                       `delivered` counts the events delivered so far, frames
                       left out. `lost` with `done`: it ended with no result.
     sendFallback(msg) while no socket is open, `send` hands the message here
                       (e.g. Stop over HTTP); returns whether it took it
     retries           reconnects after a drop (default 3; 0 for a stream
                       whose server session ends with its socket)
     backoffMs         first reconnect delay, doubled each time (default 1000)
     pollMs            between polls (default 1000)
     maxMisses         failed polls in a row before "lost" (default 10)
     silentMs          no message within this after the start → poll instead
                       (a proxy that never passes WebSockets); 0 = off */
export function openRunSocket(url, options = {}) {
  const {
    onEvent,
    onStatus,
    onEnd,
    onOpen,
    isEnd = defaultIsEnd,
    poll = null,
    sendFallback = null,
    retries = 3,
    backoffMs = 1000,
    pollMs = 1000,
    maxMisses = 10,
    silentMs = 0,
  } = options;

  let ws = null;
  let status = "connecting";
  let closed = false; // the caller closed it
  let ended = false; // onEnd has run
  let polling = false;
  let sawEnd = false;
  let attempt = 0; // reconnects used since the last good message
  let delivered = 0;
  let received = false;
  let timer = null;
  let silentTimer = null;

  const setStatus = (next) => {
    if (status === next) return;
    status = next;
    if (!closed) onStatus?.(next);
  };

  const detach = () => {
    const sock = ws;
    ws = null;
    if (!sock) return;
    sock.onopen = null;
    sock.onmessage = null;
    sock.onerror = null;
    sock.onclose = null;
    try {
      sock.close();
    } catch {
      /* already closed */
    }
  };

  const stopTimers = () => {
    clearTimeout(timer);
    clearTimeout(silentTimer);
    timer = null;
    silentTimer = null;
  };

  const finish = (reason, error) => {
    if (ended || closed) return;
    ended = true;
    stopTimers();
    detach();
    setStatus(reason === "done" ? "ended" : "lost");
    onEnd?.({ reason, error });
  };

  const deliver = (ev) => {
    if (ended || closed) return;
    if (ev?.type !== "frame") delivered += 1;
    try {
      onEvent?.(ev);
    } catch (e) {
      // A screen's handler throwing must not take the stream down with it.
      console.error(e); // eslint-disable-line no-console
    }
    if (isEnd(ev)) sawEnd = true;
  };

  const readPoll = async () => {
    const out = await poll({ delivered });
    return {
      events: Array.isArray(out?.events) ? out.events : [],
      done: Boolean(out?.done),
      lost: Boolean(out?.lost),
    };
  };

  const startPolling = () => {
    if (ended || closed) return;
    stopTimers();
    detach();
    if (!poll) {
      finish("lost");
      return;
    }
    polling = true;
    setStatus("polling");
    let misses = 0;
    const tick = async () => {
      if (ended || closed) return;
      try {
        const out = await readPoll();
        if (ended || closed) return;
        misses = 0;
        out.events.forEach(deliver);
        if (out.done || sawEnd) {
          finish(out.lost && !sawEnd ? "lost" : "done");
          return;
        }
      } catch (e) {
        if (ended || closed) return;
        misses += 1;
        if (misses >= maxMisses) {
          finish("lost", e);
          return;
        }
      }
      timer = setTimeout(tick, pollMs);
    };
    tick();
  };

  const onDrop = async () => {
    if (ended || closed || polling) return;
    if (sawEnd) {
      finish("done");
      return;
    }
    // Never shown as a failure: the run is very likely still going.
    setStatus(attempt < retries || poll ? "reconnecting" : status);
    if (poll) {
      // A run that ended while the stream was down: reconnecting would wait
      // on a server queue that has nothing more to send.
      try {
        const out = await readPoll();
        if (ended || closed || polling) return;
        if (out.done) {
          out.events.forEach(deliver);
          finish(out.lost && !sawEnd ? "lost" : "done");
          return;
        }
        // Not ended: the reconnected stream sends the rest.
      } catch {
        if (ended || closed || polling) return;
      }
    }
    if (attempt < retries) {
      attempt += 1;
      timer = setTimeout(connect, backoffMs * 2 ** (attempt - 1));
      return;
    }
    startPolling();
  };

  function connect() {
    if (ended || closed || polling) return;
    const reconnect = attempt > 0;
    let sock;
    try {
      sock = new WebSocket(url);
    } catch {
      timer = setTimeout(onDrop, 0);
      return;
    }
    ws = sock;
    sock.onopen = () => {
      if (ws !== sock || closed) return;
      setStatus("open");
      onOpen?.();
    };
    sock.onmessage = (m) => {
      if (ws !== sock || closed || polling) return;
      let ev;
      try {
        ev = JSON.parse(m.data);
      } catch {
        return;
      }
      if (reconnect && isUnknownRun(ev)) return;
      received = true;
      clearTimeout(silentTimer);
      attempt = 0; // a working stream again: the next drop gets its own retries
      setStatus("open");
      deliver(ev);
    };
    sock.onerror = () => {
      /* onclose follows */
    };
    sock.onclose = () => {
      if (ws !== sock) return;
      ws = null;
      onDrop();
    };
  }

  const handle = {
    get status() {
      return status;
    },
    get readyState() {
      return ws ? ws.readyState : SOCKET_CLOSED;
    },
    /* Sends now over the socket, else through `sendFallback` (false when
       neither takes it, e.g. while reconnecting with no fallback). */
    send(data) {
      if (closed || ended) return false;
      if (!ws || ws.readyState !== SOCKET_OPEN) {
        if (!sendFallback) return false;
        let msg = data;
        if (typeof data === "string") {
          try {
            msg = JSON.parse(data);
          } catch {
            return false;
          }
        }
        return Boolean(sendFallback(msg));
      }
      try {
        ws.send(typeof data === "string" ? data : JSON.stringify(data));
        return true;
      } catch {
        return false;
      }
    },
    /* The caller is done with it: no more callbacks. */
    close() {
      if (closed) return;
      closed = true;
      stopTimers();
      detach();
      status = "closed";
    },
  };

  connect();
  if (silentMs && poll) {
    silentTimer = setTimeout(() => {
      if (!received) startPolling();
    }, silentMs);
  }
  return handle;
}

/* One stream for the screen's life: opened when `url` is set, closed when it
   changes or the screen goes. The callbacks may change between renders. */
export function useRunSocket(url, options = {}) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const handleRef = useRef(null);
  const [status, setStatus] = useState(url ? "connecting" : "idle");

  useEffect(() => {
    if (!url) {
      setStatus("idle");
      return undefined;
    }
    const opts = optionsRef.current;
    setStatus("connecting");
    const handle = openRunSocket(url, {
      ...opts,
      onEvent: (ev) => optionsRef.current.onEvent?.(ev),
      onOpen: () => optionsRef.current.onOpen?.(),
      onEnd: (info) => optionsRef.current.onEnd?.(info),
      onStatus: (s) => {
        setStatus(s);
        optionsRef.current.onStatus?.(s);
      },
      poll: opts.poll ? (ctx) => optionsRef.current.poll(ctx) : null,
    });
    handleRef.current = handle;
    return () => {
      handle.close();
      if (handleRef.current === handle) handleRef.current = null;
    };
  }, [url]);

  const send = useCallback((data) => (handleRef.current ? handleRef.current.send(data) : false), []);
  const close = useCallback(() => handleRef.current?.close(), []);
  return { status, send, close };
}

/* Streams a screen opens as it goes (one per run, several at once): returns
   `open(url, options)` → a handle as `openRunSocket`'s. Every one still open
   is closed when the screen goes. */
export function useRunSockets() {
  const live = useRef(new Set());
  useEffect(() => {
    const store = live.current;
    return () => {
      store.forEach((h) => h.close());
      store.clear();
    };
  }, []);
  return useCallback((url, options = {}) => {
    let handle = null;
    handle = openRunSocket(url, {
      ...options,
      onEnd: (info) => {
        live.current.delete(handle);
        options.onEnd?.(info);
      },
    });
    // Closed by the screen (no onEnd): it leaves the set too.
    const close = handle.close;
    handle.close = () => {
      live.current.delete(handle);
      close();
    };
    if (handle.status !== "ended" && handle.status !== "lost") live.current.add(handle);
    return handle;
  }, []);
}
